import { useDispatch, useSelector } from "react-redux"
import { selectUsers, selectWineries, selectWineryDisplayGrid } from "../../context/selectors"
import useLabels from "../../hooks/useLabels";
import { CTA } from "../Generic";
import { useNavigate } from "react-router-dom";
import { set, setActive } from "../../context/wineries/wineriesSlice";
import { useEffect, useState } from "react";
import { getUsers, getWineries } from "../../services/utils";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { Switch } from "../FormComponents";
import { toggleWineriesDisplayGrid } from "../../context/configs/configsSlice";
import WineryTile from "./WineryTile";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "../Table";
import { SkeletonTable } from "../Skeleton/Skeleton";
import { emptyTanks } from "../../context/tanks/tanksSlice";
import { emptyLots } from "../../context/lots/lotsSlice";
import { emptyUsers, setUsers } from "../../context/users/usersSlice";
import { emptyAnalysis } from "../../context/protocols/analysisSlice";
import { emptyExp } from "../../context/expOperations/expOperationsSlice";
import { empty } from "../../context/operations/operationsSlice";
import './styles/index.scss';
import { ROLES } from "../../constants/base";
import useAuth from "../../hooks/useAuth";
import TableCell from "../Table/TableCell";

const WineriesSelector = ({
    eventCallback,
    isSelector = false,
    isMobile = false,
    isModal = false,
}) => {

    const { auth } = useAuth();
    const role = auth?.role || ROLES.GUEST;
    const wineries = useSelector(selectWineries);
    const users = useSelector(selectUsers);
    const displayModeGrid = useSelector(selectWineryDisplayGrid);
    const dispatch = useDispatch();
    const [getLabel] = useLabels();
    const navigate = useNavigate();
    const axiosPrivate = useAxiosPrivate();
    const [isWineriesLoading, setIsWineriesLoading] = useState(true);

  const columnHelper = createColumnHelper();
  const additionalColumns = !isSelector ? [
    columnHelper.accessor((row) => row?.province ?? '', {
      header: getLabel("wineryProvince"),
      cell: TableCell,
      meta: { filterVariant: "text" },
    }),
    columnHelper.accessor((row) => row?.country ?? '', {
      header: getLabel("wineryCountry"),
      cell: TableCell,
      meta: { filterVariant: "text" },
    }),
    columnHelper.accessor((row) => row?.mobile_number ?? '', {
      header: getLabel("wineryMobile"),
      cell: TableCell,
      meta: { filterVariant: "text" },
    }),
    columnHelper.accessor((row) => row?.phone_number ?? '', {
      header: getLabel("wineryPhone"),
      cell: TableCell,
      meta: { filterVariant: "text" },
    }),
  ] : [];

  const columns = [
    columnHelper.accessor('name', {
      header: getLabel("wineryName"),
      cell: TableCell,
      meta: { filterVariant: "text" },
    }),
    columnHelper.accessor("client_name", {
      header: getLabel("wineryClientName"),
      cell: TableCell,
      meta: { filterVariant: "text" },
    }),
    columnHelper.accessor(
      (row) => `${row?.address ?? ''}, ${row?.cap ?? ''} ${row?.city ?? ''}`.trim().replace(/^,\s*/, ''),
      {
        header: getLabel("wineryAddress"),
        cell: TableCell,
        meta: { filterVariant: "text" },
      }
    ),
    ...additionalColumns
  ];

    const toggleDisplayMode = () => {
        dispatch(toggleWineriesDisplayGrid())
    }
        
    const clickHandler = (data) => {
        dispatch(setActive(data?.original))
        if (eventCallback) {
            eventCallback(data?.original);

            dispatch(empty());
            dispatch(emptyExp());
            dispatch(emptyAnalysis());
            dispatch(emptyUsers());
            dispatch(emptyTanks());
            dispatch(emptyLots());
            dispatch(emptyAnalysis());
        }
    }

    const loadWineries = async () => {
        try {
            const currentWineries = await getWineries(axiosPrivate);
            dispatch(set(currentWineries))
        } finally {
            setIsWineriesLoading(false);
        }
    }

    const loadUsers = async () => {
        const currentUsers = await getUsers(axiosPrivate);
        dispatch(setUsers(currentUsers))
    }

    useEffect(() => {
        if (!wineries || wineries.length === 0) {
            loadWineries();
        } else {
            setIsWineriesLoading(false);
        }

        if (!users || users.length === 0) {
        loadUsers();
        }
    }, [])

    const renderWineriesList = () => {
        if (isWineriesLoading) {
            return <SkeletonTable colCount={!isSelector ? 7 : 3} />;
        }
        if (wineries?.length > 0) {
            return (
                <div className="wineries_list">
                    <Table
                        defaultData={[...wineries].sort((a, b) => a?.name?.localeCompare(b?.name))}
                        columns={columns}
                        isSearchBar={!isSelector}
                        isEditable={false}
                        isLegend={false}
                        rowClickHandler={!isSelector ? (row) => { navigate(`/wineries/${row.original.id}`) } : clickHandler}
                    />
                </div>
            )
        }
        return (<div className="wineries_alert">
            <h6 className="wineries_alert_title">{getLabel('noWineriesAlert')}</h6>
            {role === ROLES.CLIENT && (
            <CTA
                customClassName={'create'}
                onClick={() => {navigate('/wineries/new')}}
            >
                {getLabel('createWinery')}
            </CTA>)}
            {/* TO DO: add {" "}<Icon icon="fa-plus" /> */}
        </div>)
    }

    const renderWineriesGrid = () => {
        if (isWineriesLoading) {
            return <SkeletonTable colCount={!isSelector ? 7 : 3} />;
        }
        if (wineries?.length > 0) {
            return (
                <div className={`wineries_grid ${isModal ? 'is_modal' : ''}`}>
                    {[...wineries]?.sort((a, b) => a?.name?.localeCompare(b?.name)).map((winery) => { 
                        return <WineryTile 
                            isMobile={isMobile}
                            name={winery?.name}
                            city={winery?.city}
                            id={winery?.id}
                            clickHandler={() => clickHandler({original: winery})}
                        />
                    })}
                </div>
            )
            
        }
    }

    return (
        <div className={isSelector ? "wineries" : "wineries_list"}>
        {(!isMobile && !isSelector) && <Switch
            leftOption={{
                label: getLabel('listView')
            }}
            rightOption={{
                label: getLabel('gridView')
            }}
            toggleHandler={toggleDisplayMode}
            status={displayModeGrid}
        />}
        {(displayModeGrid || isMobile) ? renderWineriesGrid() : renderWineriesList()}
        </div>
    )
}

export {
    WineriesSelector
}